// «+ Nuevo» de Tiendas: alta de tienda o de zona en `retail-org`, una pestaña por permiso.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Controller, useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { Input, PhoneInput } from '@/kit';
import { useCreateStore, useCreateZone } from '@/domains/retail/negocios/api/useRetailOrg';
import { CreateDrawer, NewButton, type CreateDrawerTab } from '@/shared/patterns/CreateDrawer';
import { FormSection, FormSelect } from '../../inventario/components/catalog/FormBits';
import { useRosterZones } from '../../lib/useRoster';
import { useIntegrationLock } from '../../integration/components/IntegrationLock';
import {
    toRosterWriteError,
    useInvalidateRoster,
    useRosterAbilities,
} from '../../lib/rosterWrites';
import {
    EMPTY_STORE_FORM,
    EMPTY_ZONE_FORM,
    STORE_CODE_MAX,
    newStoreSchema,
    newZoneSchema,
    toCreateStoreRequest,
    toCreateZoneRequest,
    zoneSelectOptions,
    type NewStoreForm,
    type NewZoneForm,
} from '../lib/newStoreForm';

const FORM_CLASS = 'flex flex-col gap-5';

type NewStoreTab = 'STORE' | 'ZONE';

interface ZoneOption {
    value: string;
    label: string;
}

function StoreForm({
    formId,
    form,
    zoneOptions,
    zonesLoading,
    onSubmit,
}: {
    formId: string;
    form: UseFormReturn<NewStoreForm>;
    zoneOptions: ZoneOption[];
    zonesLoading: boolean;
    onSubmit: (values: NewStoreForm) => Promise<void>;
}) {
    const { errors } = form.formState;
    return (
        <form
            id={formId}
            noValidate
            autoComplete="off"
            className={FORM_CLASS}
            onSubmit={(event) => {
                void form.handleSubmit(onSubmit)(event);
            }}
        >
            <FormSection title="Tienda">
                <div className="grid grid-cols-[1fr_140px] gap-3">
                    <Input
                        label="Nombre"
                        required
                        placeholder="Ej. Plaza Centro"
                        error={errors.name?.message}
                        {...form.register('name')}
                    />
                    <Input
                        label="Clave"
                        required
                        maxLength={STORE_CODE_MAX}
                        hint={`Hasta ${String(STORE_CODE_MAX)} caracteres.`}
                        error={errors.code?.message}
                        {...form.register('code')}
                    />
                </div>
                <div className="grid grid-cols-2 gap-3">
                    <Controller
                        control={form.control}
                        name="zoneId"
                        render={({ field, fieldState }) => (
                            <FormSelect
                                label="Zona"
                                value={field.value}
                                onChange={field.onChange}
                                options={zoneOptions}
                                disabled={zonesLoading}
                                placeholder={zonesLoading ? 'Cargando zonas…' : 'Sin zona'}
                                error={fieldState.error?.message}
                            />
                        )}
                    />
                    <Controller
                        control={form.control}
                        name="phone"
                        render={({ field, fieldState }) => (
                            <PhoneInput
                                ref={field.ref}
                                name={field.name}
                                label="Teléfono"
                                required
                                value={field.value}
                                onValueChange={field.onChange}
                                onBlur={field.onBlur}
                                error={fieldState.error?.message}
                            />
                        )}
                    />
                </div>
            </FormSection>
            <FormSection title="Dirección">
                <div className="grid grid-cols-2 gap-3">
                    <Input
                        label="Calle y número"
                        required
                        error={errors.address1?.message}
                        {...form.register('address1')}
                    />
                    <Input
                        label="Colonia"
                        error={errors.neighborhood?.message}
                        {...form.register('neighborhood')}
                    />
                    <Input
                        label="Ciudad"
                        required
                        error={errors.city?.message}
                        {...form.register('city')}
                    />
                    <Input
                        label="Estado"
                        required
                        error={errors.state?.message}
                        {...form.register('state')}
                    />
                    <Input
                        label="Código postal"
                        required
                        inputMode="numeric"
                        maxLength={5}
                        error={errors.zipCode?.message}
                        {...form.register('zipCode')}
                    />
                </div>
            </FormSection>
            <FormSection title="Ubicación">
                <div className="grid grid-cols-2 gap-3">
                    <Input
                        label="Latitud"
                        required
                        inputMode="decimal"
                        placeholder="19.4326"
                        error={errors.lat?.message}
                        {...form.register('lat')}
                    />
                    <Input
                        label="Longitud"
                        required
                        inputMode="decimal"
                        placeholder="-99.1332"
                        error={errors.lng?.message}
                        {...form.register('lng')}
                    />
                </div>
                <p className="-mt-1 text-[10.5px] text-muted-foreground">
                    Se usa para encontrar la tienda más cercana. Cópiala de Google Maps: clic
                    derecho sobre la tienda y elige las coordenadas.
                </p>
            </FormSection>
        </form>
    );
}

function ZoneForm({
    formId,
    form,
    onSubmit,
}: {
    formId: string;
    form: UseFormReturn<NewZoneForm>;
    onSubmit: (values: NewZoneForm) => Promise<void>;
}) {
    const { errors } = form.formState;
    return (
        <form
            id={formId}
            noValidate
            autoComplete="off"
            className={FORM_CLASS}
            onSubmit={(event) => {
                void form.handleSubmit(onSubmit)(event);
            }}
        >
            <FormSection title="Zona">
                <div className="grid grid-cols-2 gap-3">
                    <Input
                        label="Nombre"
                        required
                        placeholder="Ej. Zona Norte"
                        hint="No se puede cambiar después."
                        error={errors.name?.message}
                        {...form.register('name')}
                    />
                    <Input
                        label="Región"
                        required
                        placeholder="Ej. Bajío"
                        error={errors.region?.message}
                        {...form.register('region')}
                    />
                </div>
                <p className="text-[10.5px] text-muted-foreground">
                    Después asígnala a sus tiendas desde la pestaña Tienda.
                </p>
            </FormSection>
        </form>
    );
}

const NO_ERRORS: Record<NewStoreTab, string | null> = { STORE: null, ZONE: null };

/** Tras crear una zona, si hay pestaña Tienda se vuelve a ella con la zona ya en el select. */
function NewStoreEditor({
    retailerId,
    canCreateStore,
    canCreateZone,
    onClose,
}: {
    retailerId: string;
    canCreateStore: boolean;
    canCreateZone: boolean;
    onClose: () => void;
}) {
    const [tab, setTab] = useState<NewStoreTab>(canCreateStore ? 'STORE' : 'ZONE');
    const [errors, setErrors] = useState(NO_ERRORS);
    const createStore = useCreateStore(retailerId);
    const createZone = useCreateZone(retailerId);
    const invalidateRoster = useInvalidateRoster();
    const zones = useRosterZones(retailerId);
    const zoneOptions = useMemo(() => zoneSelectOptions(zones.data ?? []), [zones.data]);
    const storeForm = useForm<NewStoreForm>({
        resolver: zodResolver(newStoreSchema),
        defaultValues: EMPTY_STORE_FORM,
    });
    const zoneForm = useForm<NewZoneForm>({
        resolver: zodResolver(newZoneSchema),
        defaultValues: EMPTY_ZONE_FORM,
    });
    // La zona nueva se elige hasta que su opción existe: antes, el select la descarta por desconocida.
    const pendingZoneId = useRef<string | null>(null);
    useEffect(() => {
        const zoneId = pendingZoneId.current;
        if (zoneId === null || !zoneOptions.some((option) => option.value === zoneId)) return;
        if (storeForm.getValues('zoneId') === '') storeForm.setValue('zoneId', zoneId);
        pendingZoneId.current = null;
    }, [zoneOptions, storeForm]);
    const setError = (key: NewStoreTab, message: string | null) => {
        setErrors((current) => ({ ...current, [key]: message }));
    };

    const submitStore = async (values: NewStoreForm) => {
        setError('STORE', null);
        try {
            await createStore.mutateAsync(toCreateStoreRequest(values));
        } catch (failure) {
            const mapped = toRosterWriteError(failure);
            if (mapped.field === 'code' || mapped.field === 'zoneId') {
                storeForm.setError(mapped.field, { message: mapped.message });
            }
            setError('STORE', mapped.message);
            return;
        }
        toast.success('Tienda dada de alta', { description: values.name.trim() });
        await invalidateRoster();
        onClose();
    };

    const submitZone = async (values: NewZoneForm) => {
        setError('ZONE', null);
        try {
            const created = await createZone.mutateAsync(toCreateZoneRequest(values));
            pendingZoneId.current = created.zoneId;
        } catch (failure) {
            setError('ZONE', toRosterWriteError(failure).message);
            return;
        }
        toast.success('Zona dada de alta', { description: values.name.trim() });
        await invalidateRoster();
        if (!canCreateStore) {
            onClose();
            return;
        }
        zoneForm.reset(EMPTY_ZONE_FORM);
        setTab('STORE');
    };

    const tabs: CreateDrawerTab<NewStoreTab>[] = [];
    if (canCreateStore) {
        tabs.push({
            key: 'STORE',
            label: 'Tienda',
            render: (formId) => (
                <StoreForm
                    formId={formId}
                    form={storeForm}
                    zoneOptions={zoneOptions}
                    zonesLoading={zones.isPending}
                    onSubmit={submitStore}
                />
            ),
        });
    }
    if (canCreateZone) {
        tabs.push({
            key: 'ZONE',
            label: 'Zona',
            render: (formId) => <ZoneForm formId={formId} form={zoneForm} onSubmit={submitZone} />,
        });
    }

    return (
        <CreateDrawer<NewStoreTab>
            title={tab === 'STORE' ? 'Nueva tienda' : 'Nueva zona'}
            tabs={tabs}
            activeTab={tab}
            onTabChange={setTab}
            submitLabel="Dar de alta"
            isPending={tab === 'STORE' ? createStore.isPending : createZone.isPending}
            error={errors[tab]}
            onClose={onClose}
            keepMounted
        />
    );
}

/** «+ Nuevo» con una pestaña por permiso (`retail.store.create`, `retail.zone.create`); sin ninguno, nada. */
export function NewStoreButton({ retailerId }: { retailerId: string | null }) {
    const { canCreateStore, canCreateZone } = useRosterAbilities();
    const lock = useIntegrationLock();
    const [openCount, setOpenCount] = useState(0);
    const [open, setOpen] = useState(false);
    if (!canCreateStore && !canCreateZone) return null;
    return (
        <>
            <NewButton
                disabled={retailerId === null || lock.locked}
                title={
                    retailerId === null
                        ? 'Necesitas una cadena asignada para dar de alta'
                        : lock.reason
                }
                onClick={() => {
                    setOpenCount((count) => count + 1);
                    setOpen(true);
                }}
            />
            {open && retailerId !== null ? (
                <NewStoreEditor
                    key={openCount}
                    retailerId={retailerId}
                    canCreateStore={canCreateStore}
                    canCreateZone={canCreateZone}
                    onClose={() => {
                        setOpen(false);
                    }}
                />
            ) : null}
        </>
    );
}
